import { Button, Card, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import { httpsCallable } from 'firebase/functions';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { functions } from '../../lib/firebase';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { useSessions } from '../sessions-admin';
import {
  applyMapping,
  autoDetectMapping,
  buildInternalCsv,
  emptyMapping,
  extractSessionNames,
  extractUserCount,
  importModeToDistrictMode,
  summarizeLocation,
  type ColumnMapping,
  type MappedRow,
  type MappingFieldKey,
} from './columnMapping';
import { ColumnMappingPanel } from './ColumnMappingPanel';
import { buildImportPreview, type ImportMode, type ImportPreview } from './csvParser';
import { DrivePhotosPanel } from './DrivePhotosPanel';
import { matchPhotos } from './photoMatcher';
import { uploadLocalPhotos, type PhotoUploadProgress } from './photoUploader';
import { SheetsImportPanel } from './SheetsImportPanel';
import { sheetsUrlToCsvUrl } from './sheetsUrl';

/**
 * Sprint AY Part 3: importExecutor callable 結線。
 * Sprint BG-DataSettings 20: participant / session mode + Sheets API + Drive 連携で完全化。
 * Admin Wave AW7 (docs/108 Critical #9 / High1-3): カラムマッピング UI + locationCombined +
 *   ECW 固有列 + ローカル写真アップロード + セッション割当 + リッチプレビュー。
 *
 * functions/ から直接 import せず、shared-types に依存もせず、callable の input/output shape は
 * 本 file 内で local type 宣言する (CLAUDE.md boundary 規約: apps/admin → functions/ 禁止)。
 *
 * AW7 設計: post-* mode は CSV ヘッダ → 投稿フィールドを client-side で rename し、importExecutor の
 * KNOWN_COLUMNS に合致した CSV を再構築して送る。これにより functions/backend 変更ゼロで
 * 任意 CSV ヘッダ (AppSheet 等) のインポートを実現する。participant / session は固定 schema 直 import。
 */
interface ImportExecutorInput {
  districtId: string;
  csvText: string;
  mode: 'post-bousai' | 'post-walker' | 'post-ecw';
  dryRun?: boolean;
}
interface ImportExecutorResult {
  ok: true;
  dryRun: boolean;
  totalRows: number;
  validRows: number;
  successCount: number;
  skipCount: number;
  errors: { lineNumber: number; message: string }[];
}

interface ImportParticipantsInput {
  districtId: string;
  csvText: string;
  dryRun?: boolean;
}
interface ImportParticipantsResult {
  ok: true;
  dryRun: boolean;
  totalRows: number;
  validRows: number;
  mapping: Record<string, string>;
  createdCount: number;
  reusedCount: number;
  errors: { lineNumber: number; userName: string; message: string }[];
}

interface ImportSessionsInput {
  districtId: string;
  csvText: string;
  dryRun?: boolean;
}
interface ImportSessionsResult {
  ok: true;
  dryRun: boolean;
  totalRows: number;
  validRows: number;
  mapping: Record<string, string>;
  createdCount: number;
  reusedCount: number;
  errors: { lineNumber: number; name: string; message: string }[];
}

/**
 * 本 sprint で実行可能な全 mode (Sprint BG-DataSettings 20: participant / session 追加)。
 *
 * 【廃止予定を含む】post-bousai / post-walker / post-ecw の組込 mode 別取込は old2 データ
 * 移行ツールとしての性質上のハードコード (モードドリブン化 2026-07-24 の対象外)。
 * 新規 custom mode 地区への過去データ取込が必要になった場合は fields[] 駆動の
 * generic 取込 mode の追加を検討する (issue 参照)。
 */
const SUPPORTED_EXEC_MODES: ImportMode[] = [
  'post-bousai',
  'post-walker',
  'post-ecw',
  'participant',
  'session',
];

/** post-* mode 判定 (カラムマッピング / 写真 / セッション割当を出すのは post-* のみ)。 */
function isPostMode(mode: ImportMode): mode is 'post-bousai' | 'post-walker' | 'post-ecw' {
  return mode === 'post-bousai' || mode === 'post-walker' || mode === 'post-ecw';
}

/** 写真ソース。 */
type PhotoSource = 'none' | 'local';

/**
 * post-* / participant / session の callable 結果を統一表示するための内部 shape。
 */
interface UnifiedImportResult {
  dryRun: boolean;
  totalRows: number;
  validRows: number;
  primaryCount: number;
  secondaryCount: number;
  primaryLabel: string;
  secondaryLabel: string;
  errors: { lineNumber: number; message: string }[];
}

function toUnifiedFromExecutor(r: ImportExecutorResult): UnifiedImportResult {
  return {
    dryRun: r.dryRun,
    totalRows: r.totalRows,
    validRows: r.validRows,
    primaryCount: r.successCount,
    secondaryCount: r.skipCount,
    primaryLabel: '投入',
    secondaryLabel: 'skip',
    errors: r.errors,
  };
}
function toUnifiedFromParticipants(r: ImportParticipantsResult): UnifiedImportResult {
  return {
    dryRun: r.dryRun,
    totalRows: r.totalRows,
    validRows: r.validRows,
    primaryCount: r.createdCount,
    secondaryCount: r.reusedCount,
    primaryLabel: '作成',
    secondaryLabel: '既存 reuse',
    errors: r.errors.map((e) => ({
      lineNumber: e.lineNumber,
      message: e.userName ? `[${e.userName}] ${e.message}` : e.message,
    })),
  };
}
function toUnifiedFromSessions(r: ImportSessionsResult): UnifiedImportResult {
  return {
    dryRun: r.dryRun,
    totalRows: r.totalRows,
    validRows: r.validRows,
    primaryCount: r.createdCount,
    secondaryCount: r.reusedCount,
    primaryLabel: '作成',
    secondaryLabel: '既存 reuse',
    errors: r.errors.map((e) => ({
      lineNumber: e.lineNumber,
      message: e.name ? `[${e.name}] ${e.message}` : e.message,
    })),
  };
}

/**
 * Import 画面 (admin 第 25 弾 → Sprint BG-DataSettings 20 → Admin Wave AW7)。
 *
 * 認可: master または該当 district admin。実 submit (importExecutor 等 callable) は master 専用。
 */
export function ImportPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const sessionsState = useSessions(districtId ?? null);
  const { toast } = useToast();
  const [mode, setMode] = useState<ImportMode>('post-bousai');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [csvText, setCsvText] = useState<string>('');

  // AW7: カラムマッピング (post-* のみ)
  const [mapping, setMapping] = useState<ColumnMapping>(emptyMapping());
  const [mappedRows, setMappedRows] = useState<MappedRow[] | null>(null);
  const [mappingWarnings, setMappingWarnings] = useState<{ rowIndex: number; message: string }[]>(
    [],
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [includeNoLocation, setIncludeNoLocation] = useState(true);

  // AW7: 写真ソース (local / none)
  const [photoSource, setPhotoSource] = useState<PhotoSource>('none');
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoProgress, setPhotoProgress] = useState<PhotoUploadProgress | null>(null);

  // callable submit 状態
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<UnifiedImportResult | null>(null);
  const [submitState, setSubmitState] = useState<
    'idle' | 'dry-running' | 'uploading-photos' | 'submitting'
  >('idle');
  const [submitResult, setSubmitResult] = useState<UnifiedImportResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Google Sheets URL からの取込
  const [sheetsUrl, setSheetsUrl] = useState('');
  const [sheetsFetching, setSheetsFetching] = useState(false);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canImport = isMaster || isDistrictAdminOfThis;
  const canExecute = isMaster && SUPPORTED_EXEC_MODES.includes(mode);

  const districtMode = districtState.status === 'ready' ? districtState.district.mode : null;
  const aiEnabled =
    districtState.status === 'ready' && districtState.district.aiAnalysis?.enabled === true;
  const sessions = sessionsState.status === 'ready' ? sessionsState.items : [];

  // post-* mode かつ preview 済 → マッピング工程を出す
  const showMapping = isPostMode(mode) && preview !== null && preview.headers.length > 0;
  // post-* は internal CSV を送る、それ以外は raw CSV
  const postModeDistrict = importModeToDistrictMode(mode);

  // 利用可能な mode
  const AVAILABLE_MODES: { value: ImportMode; label: string; show: boolean }[] = [
    {
      value: 'post-bousai',
      label: '投稿 (bousai 防災)',
      show: districtMode === null || districtMode === 'bousai',
    },
    {
      value: 'post-walker',
      label: '投稿 (walker ウォーカブル)',
      show: districtMode === null || districtMode === 'walker',
    },
    {
      value: 'post-ecw',
      label: '投稿 (ecw)',
      show: districtMode === null || districtMode === 'ecw' || districtMode === 'kids-bousai',
    },
    { value: 'participant', label: '参加者 (participant)', show: true },
    { value: 'session', label: 'セッション (session)', show: true },
  ];

  /** preview をリセットしてマッピング状態も初期化 (mode 変更 / 新規 CSV 読込時)。 */
  function resetForNewSource() {
    setPreview(null);
    setParseError(null);
    setCsvText('');
    setMappedRows(null);
    setMappingWarnings([]);
    setDryRunResult(null);
    setSubmitResult(null);
    setSubmitError(null);
    setPhotoProgress(null);
  }

  /** CSV テキストを parse → preview + post-* なら自動マッピング推定。 */
  function ingestCsv(text: string, label: string, viaToast = false) {
    resetForNewSource();
    setFileName(label);
    try {
      const p = buildImportPreview(text, mode);
      setPreview(p);
      setCsvText(text);
      if (isPostMode(mode)) {
        setMapping(autoDetectMapping(p.headers));
      }
      if (viaToast) toast('success', `${label}: ${p.totalRows} 行を取得`);
    } catch (e) {
      setParseError(`parse エラー: ${(e as Error).message}`);
    }
  }

  function handleFile(file: File) {
    setFileName(file.name);
    setParseError(null);
    setPreview(null);
    setCsvText('');
    setMappedRows(null);
    const reader = new FileReader();
    reader.onerror = () => {
      setParseError('ファイル読込に失敗しました');
    };
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      ingestCsv(text, file.name);
    };
    reader.readAsText(file, 'utf-8');
  }

  function setMappingField(key: MappingFieldKey, value: string | null) {
    setMapping((prev) => ({ ...prev, [key]: value }));
    setMappedRows(null); // マッピング変更でプレビューを無効化
  }

  /** AW7: カラムマッピングを適用して MappedRow[] + 内部 CSV を生成。 */
  function handleApplyMapping() {
    if (!preview) return;
    const result = applyMapping(preview.rows, mapping, postModeDistrict, {
      sessionId,
      includeNoLocation,
    });
    setMappedRows(result.rows);
    setMappingWarnings(result.warnings);
    setDryRunResult(null);
    setSubmitResult(null);
    setSubmitError(null);
    // 写真を後段でアップロードするため、ここでは photo 列無しの CSV を仮構築
    setCsvText(buildInternalCsv(result.rows));
    if (result.rows.length === 0) {
      toast('warning', '有効な行がありません (タイトル/コメントが全行空など)');
    } else {
      toast('success', `${result.rows.length} 件をマッピングしました`);
    }
  }

  function handlePhotoFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files) return;
    setPhotoFiles(Array.from(files));
  }

  /**
   * mode に応じて適切な callable を呼出し、UnifiedImportResult に正規化して返す。
   * post-* は引数 csv (内部名 CSV) を、participant/session は state の raw csvText を送る。
   */
  async function invokeImportCallable(dryRun: boolean, csv: string): Promise<UnifiedImportResult> {
    if (mode === 'participant') {
      const callable = httpsCallable<ImportParticipantsInput, ImportParticipantsResult>(
        functions,
        'importParticipants',
      );
      const res = await callable({ districtId: districtId!, csvText: csv, dryRun });
      return toUnifiedFromParticipants(res.data);
    }
    if (mode === 'session') {
      const callable = httpsCallable<ImportSessionsInput, ImportSessionsResult>(
        functions,
        'importSessions',
      );
      const res = await callable({ districtId: districtId!, csvText: csv, dryRun });
      return toUnifiedFromSessions(res.data);
    }
    const callable = httpsCallable<ImportExecutorInput, ImportExecutorResult>(
      functions,
      'importExecutor',
    );
    const res = await callable({
      districtId: districtId!,
      csvText: csv,
      mode: mode as 'post-bousai' | 'post-walker' | 'post-ecw',
      dryRun,
    });
    return toUnifiedFromExecutor(res.data);
  }

  /** 実行可能か (post-* はマッピング適用済が必須)。 */
  const execReady =
    canExecute &&
    submitState === 'idle' &&
    (isPostMode(mode)
      ? mappedRows !== null && mappedRows.length > 0
      : !!csvText && !!preview && preview.validRows > 0);

  /** 実行 button 押下 → まず dryRun=true で件数確認 → ConfirmDialog 表示。 */
  async function handleRequestExecute() {
    if (!districtId || !csvText || !canExecute) return;
    setSubmitState('dry-running');
    setSubmitError(null);
    setSubmitResult(null);
    try {
      const unified = await invokeImportCallable(true, csvText);
      setDryRunResult(unified);
      setConfirmOpen(true);
    } catch (e) {
      setSubmitError(`dryRun 失敗: ${(e as Error).message}`);
    } finally {
      setSubmitState('idle');
    }
  }

  /**
   * ConfirmDialog で「実行」押下 → (post-* かつローカル写真あれば) 写真アップロード → 本投入。
   */
  async function handleConfirmExecute() {
    if (!districtId || !canExecute) return;
    setConfirmOpen(false);
    setSubmitError(null);

    let finalCsv = csvText;

    // AW7: ローカル写真アップロード → photoPath/photoUrl を内部行に注入 → CSV 再構築
    if (isPostMode(mode) && mappedRows && photoSource === 'local' && photoFiles.length > 0) {
      setSubmitState('uploading-photos');
      try {
        const { matched } = matchPhotos(mappedRows, photoFiles);
        if (matched.size > 0) {
          const uploaded = await uploadLocalPhotos(districtId, mappedRows, matched, (p) =>
            setPhotoProgress(p),
          );
          const enriched = mappedRows.map((r) => {
            const info = uploaded.get(r.rowIndex);
            if (!info) return r;
            return {
              ...r,
              internal: {
                ...r.internal,
                photoPath: info.photoPath,
                photoThumbPath: info.photoThumbPath,
                ...(info.photoUrl ? { photoUrl: info.photoUrl } : {}),
                ...(info.photoThumbUrl ? { photoThumbUrl: info.photoThumbUrl } : {}),
              },
            };
          });
          setMappedRows(enriched);
          finalCsv = buildInternalCsv(enriched);
          setCsvText(finalCsv);
          toast('success', `写真 ${uploaded.size} 件をアップロードしました`);
        }
      } catch (e) {
        setSubmitError(`写真アップロード失敗: ${(e as Error).message}`);
        setSubmitState('idle');
        return;
      }
    }

    setSubmitState('submitting');
    try {
      const unified = await invokeImportCallable(false, finalCsv);
      setSubmitResult(unified);
    } catch (e) {
      setSubmitError(`実 import 失敗: ${(e as Error).message}`);
    } finally {
      setSubmitState('idle');
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  function handleExternalCsv(text: string, label: string) {
    ingestCsv(text, label, true);
  }

  async function handleFetchSheets() {
    resetForNewSource();
    setFileName(null);
    setSheetsFetching(true);
    try {
      const csvUrl = sheetsUrlToCsvUrl(sheetsUrl);
      const res = await fetch(csvUrl, { credentials: 'omit' });
      if (!res.ok) {
        throw new Error(
          `HTTP ${res.status}: ${res.statusText} (sheet が公開設定されていない可能性があります)`,
        );
      }
      const text = await res.text();
      ingestCsv(text, `(Google Sheets: ${csvUrl})`, true);
    } catch (e) {
      setParseError(`Google Sheets 取得失敗: ${(e as Error).message}`);
      toast('error', `Google Sheets 取得失敗: ${(e as Error).message}`);
    } finally {
      setSheetsFetching(false);
    }
  }

  // ===== リッチプレビュー統計 (post-* かつマッピング適用済) =====
  const locStats = mappedRows ? summarizeLocation(mappedRows) : null;
  const photoFilenameCount = mappedRows ? mappedRows.filter((r) => r.photoFilename).length : 0;
  const photoMatchCount =
    mappedRows && photoSource === 'local' && photoFiles.length > 0
      ? matchPhotos(mappedRows, photoFiles).matched.size
      : 0;
  const sessionNames = mappedRows ? extractSessionNames(mappedRows) : [];
  const userCount = mappedRows ? extractUserCount(mappedRows) : 0;
  const agreeTotal = mappedRows ? mappedRows.reduce((s, r) => s + r.likeItCount, 0) : 0;

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          データインポート
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Admin Wave AW7: カラムマッピング (任意 CSV ヘッダ → 投稿フィールド) + ローカル写真
          アップロード + 結合位置列 + ECW 固有列 + セッション割当 + リッチプレビュー。
        </p>
      </header>

      {!canImport && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区のインポート権限がありません (role: {authState.role ?? 'none'}、地区:{' '}
            {districtId})。
          </p>
        </Card>
      )}

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">1. インポート種別</h2>
        <div className="flex flex-wrap gap-2">
          {AVAILABLE_MODES.filter((m) => m.show).map((m) => (
            <label
              key={m.value}
              className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
            >
              <input
                type="radio"
                name="import-mode"
                value={m.value}
                checked={mode === m.value}
                onChange={() => {
                  setMode(m.value);
                  resetForNewSource();
                  setFileName(null);
                }}
                disabled={!canImport}
              />
              {m.label}
            </label>
          ))}
        </div>
      </Card>

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">
          2-A. Google Sheets URL から取込 (公開シート)
        </h2>
        <p className="text-xs text-slate-500 mb-3">
          シートを「リンクを知っている全員に閲覧可」または「公開ウェブ」に設定した上で URL を貼付。
          内部で <code>/export?format=csv&amp;gid=&lt;n&gt;</code> に変換して CSV を fetch → 下記
          preview に流す。
        </p>
        <div className="flex gap-2 items-center flex-wrap">
          <input
            type="url"
            value={sheetsUrl}
            onChange={(e) => setSheetsUrl(e.target.value)}
            disabled={!canImport || sheetsFetching}
            placeholder="https://docs.google.com/spreadsheets/d/<id>/edit#gid=0"
            className="flex-1 min-w-0 min-h-touch px-3 border border-slate-300 rounded text-sm font-mono"
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => void handleFetchSheets()}
            disabled={!canImport || sheetsFetching || !sheetsUrl.trim()}
          >
            {sheetsFetching ? '取得中…' : '取得して preview'}
          </Button>
        </div>
      </Card>

      <SheetsImportPanel enabled={canImport} onCsvLoaded={handleExternalCsv} />

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">2-C. CSV ファイルを選択</h2>
        <p className="text-xs text-slate-500 mb-3">
          UTF-8 エンコードの CSV ファイル (RFC 4180 minimum 準拠、引用符 / 改行 in quote /
          &quot;&quot; エスケープ対応)。
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          disabled={!canImport}
          className="text-sm"
        />
        {fileName && (
          <p className="text-xs text-slate-600 mt-2">
            選択中: <code>{fileName}</code>
          </p>
        )}
      </Card>

      {parseError && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">{parseError}</p>
        </Card>
      )}

      {/* AW7: カラムマッピング (post-* のみ) */}
      {showMapping && preview && (
        <ColumnMappingPanel
          headers={preview.headers}
          mapping={mapping}
          mode={postModeDistrict}
          disabled={!canImport}
          onChange={setMappingField}
          onAutoDetect={() => {
            setMapping(autoDetectMapping(preview.headers));
            setMappedRows(null);
          }}
          onClear={() => {
            setMapping(emptyMapping());
            setMappedRows(null);
          }}
        />
      )}

      {/* AW7: 写真ソース + セッション割当 (post-* のみ) */}
      {showMapping && (
        <Card padding="lg" className="mb-4">
          <h2 className="text-base font-bold text-slate-900 mb-3">2-D. 写真 / セッション</h2>

          <div className="mb-4">
            <h3 className="text-xs font-semibold text-slate-600 mb-2">写真ソース</h3>
            <div className="flex flex-wrap gap-2 mb-2">
              {[
                { value: 'none' as const, label: '写真なし' },
                { value: 'local' as const, label: 'ローカルファイル' },
              ].map((s) => (
                <label
                  key={s.value}
                  className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
                >
                  <input
                    type="radio"
                    name="photo-source"
                    value={s.value}
                    checked={photoSource === s.value}
                    onChange={() => setPhotoSource(s.value)}
                    disabled={!canImport}
                  />
                  {s.label}
                </label>
              ))}
            </div>
            {photoSource === 'local' && (
              <div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoFiles}
                  className="hidden"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={!canImport}
                >
                  写真ファイルを選択（複数可）
                </Button>
                {photoFiles.length > 0 && (
                  <p className="text-xs text-success-700 mt-2">
                    {photoFiles.length} ファイル選択済み
                    {mappedRows && photoFilenameCount > 0 && (
                      <>
                        {' '}
                        / 写真ファイル名列とのマッチ: {photoMatchCount} / {photoFilenameCount} 件
                      </>
                    )}
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  CSV の「写真ファイル名」列とファイル名を 4 方式 (完全一致 / MAP_Images 除去 /
                  basename / rowId プレフィックス) でマッチング。原寸 + サムネ (canvas
                  内製、長辺400px) を Storage に並列アップロードします。
                </p>
              </div>
            )}
          </div>

          <div className="mb-4">
            <h3 className="text-xs font-semibold text-slate-600 mb-2">セッション割当</h3>
            <select
              value={sessionId ?? ''}
              onChange={(e) => {
                setSessionId(e.target.value || null);
                setMappedRows(null);
              }}
              disabled={!canImport}
              className="min-h-touch px-2 border border-slate-300 rounded text-sm bg-white"
            >
              <option value="">なし（セッション未指定）</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              選択すると全行に一律 sessionId が付与されます (CSV の参加グループ列とは独立)。
            </p>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={includeNoLocation}
                onChange={(e) => {
                  setIncludeNoLocation(e.target.checked);
                  setMappedRows(null);
                }}
                disabled={!canImport}
              />
              位置情報なしの行も暫定座標 (0, 0) で登録する (off で除外)
            </label>
          </div>

          <Button
            type="button"
            onClick={handleApplyMapping}
            disabled={!canImport || (!mapping.title && !mapping.comment)}
          >
            マッピング適用してプレビュー生成
          </Button>
          {!mapping.title && !mapping.comment && (
            <p className="text-xs text-danger-700 mt-2">
              タイトル列またはコメント列のマッピングが必要です。
            </p>
          )}
        </Card>
      )}

      {/* AW7: リッチプレビュー (post-* マッピング適用後) */}
      {isPostMode(mode) && mappedRows && locStats && (
        <Card padding="lg" className="mb-4">
          <h2 className="text-base font-bold text-slate-900 mb-3">3. リッチプレビュー</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
            <Stat
              label="インポート対象"
              value={`${mappedRows.length} 件`}
              color="text-primary-700"
            />
            <Stat
              label="警告行"
              value={`${mappingWarnings.length} 件`}
              color={mappingWarnings.length > 0 ? 'text-warning-700' : 'text-slate-500'}
            />
            <Stat
              label="写真マッチ"
              value={photoSource === 'none' ? '—' : `${photoMatchCount} 件`}
              color="text-slate-700"
            />
            <Stat
              label="セッション"
              value={
                sessionId ? (sessions.find((s) => s.id === sessionId)?.name ?? sessionId) : 'なし'
              }
              color="text-slate-700"
            />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-4 text-sm">
            <p className="font-medium text-slate-700 mb-1">位置情報の解決状況</p>
            <div className="flex gap-4 text-xs flex-wrap">
              <span className="text-success-700">緯度/経度列: {locStats.latLng} 件</span>
              <span className="text-primary-700">結合列 (location): {locStats.combined} 件</span>
              {locStats.none > 0 && (
                <span className="text-danger-700">位置なし: {locStats.none} 件</span>
              )}
            </div>
          </div>

          {(userCount > 0 || sessionNames.length > 0 || agreeTotal > 0) && (
            <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-4 text-sm">
              <p className="font-medium text-slate-700 mb-2">抽出統計</p>
              <div className="grid grid-cols-1 gap-1 text-xs">
                {userCount > 0 && (
                  <div className="text-primary-700">
                    ユニークユーザー (投稿者 + いいね): {userCount} 人
                  </div>
                )}
                {sessionNames.length > 0 && (
                  <div>
                    <span className="text-primary-700">参加グループ: </span>
                    {sessionNames.map((n, i) => (
                      <span
                        key={i}
                        className="inline-block bg-primary-100 text-primary-800 px-1.5 py-0.5 rounded mr-1 mb-1"
                      >
                        {n}
                      </span>
                    ))}
                  </div>
                )}
                {agreeTotal > 0 && (
                  <div className="text-primary-700">いいね合計: {agreeTotal} 件</div>
                )}
              </div>
            </div>
          )}

          {aiEnabled && photoFilenameCount > 0 && (
            <div className="bg-warning-50 border border-warning-300 rounded p-3 mb-4 text-sm text-warning-800">
              ⚠ この地区は AI 解析が有効です。写真付き投稿 {photoFilenameCount} 件が AI
              解析キューに入ります。大量インポートの場合は一時的に AI
              解析を無効化することを推奨します。
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-2 py-1 border border-slate-200 text-left">#</th>
                  <th className="px-2 py-1 border border-slate-200 text-left">タイトル</th>
                  <th className="px-2 py-1 border border-slate-200 text-left">投稿者</th>
                  <th className="px-2 py-1 border border-slate-200 text-left">緯度</th>
                  <th className="px-2 py-1 border border-slate-200 text-left">経度</th>
                  <th className="px-2 py-1 border border-slate-200 text-left">位置取得</th>
                  <th className="px-2 py-1 border border-slate-200 text-left">写真ファイル</th>
                  <th className="px-2 py-1 border border-slate-200 text-left">日時</th>
                </tr>
              </thead>
              <tbody>
                {mappedRows.slice(0, 20).map((r, i) => (
                  <tr
                    key={i}
                    className={`even:bg-slate-50 ${r.locationSource === 'none' ? 'bg-warning-50' : ''}`}
                  >
                    <td className="px-2 py-1 border border-slate-200">{i + 1}</td>
                    <td className="px-2 py-1 border border-slate-200 max-w-[12rem] truncate">
                      {r.title}
                    </td>
                    <td className="px-2 py-1 border border-slate-200">{r.authorNickname}</td>
                    <td className="px-2 py-1 border border-slate-200 font-mono">
                      {r.locationSource !== 'none' && r.latitude !== null
                        ? r.latitude.toFixed(4)
                        : '—'}
                    </td>
                    <td className="px-2 py-1 border border-slate-200 font-mono">
                      {r.locationSource !== 'none' && r.longitude !== null
                        ? r.longitude.toFixed(4)
                        : '—'}
                    </td>
                    <td className="px-2 py-1 border border-slate-200">
                      {r.locationSource === 'latLng' && (
                        <span className="text-success-700">緯度/経度</span>
                      )}
                      {r.locationSource === 'combined' && (
                        <span className="text-primary-700">結合列</span>
                      )}
                      {r.locationSource === 'none' && <span className="text-danger-700">なし</span>}
                    </td>
                    <td className="px-2 py-1 border border-slate-200 max-w-[10rem] truncate text-slate-600">
                      {r.photoFilename ?? '—'}
                    </td>
                    <td className="px-2 py-1 border border-slate-200">
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString('ja-JP') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {mappedRows.length > 20 && (
              <p className="text-xs text-slate-500 mt-1">他 {mappedRows.length - 20} 件…</p>
            )}
          </div>

          {mappingWarnings.length > 0 && (
            <details className="mt-3">
              <summary className="text-sm text-warning-700 cursor-pointer">
                警告行を表示（{mappingWarnings.length} 件）
              </summary>
              <ul className="mt-1 text-xs text-warning-700 max-h-40 overflow-auto list-disc pl-5">
                {mappingWarnings.slice(0, 100).map((w, i) => (
                  <li key={i}>
                    行 {w.rowIndex + 2}: {w.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Card>
      )}

      {/* participant / session 用の素 preview (旧来表示、マッピングなし) */}
      {!isPostMode(mode) && preview && (
        <Card padding="lg" className="mb-4">
          <h2 className="text-base font-bold text-slate-900 mb-3">3. プレビュー</h2>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mb-3">
            <dt className="text-slate-500">mode</dt>
            <dd className="text-slate-800">
              <code>{preview.mode}</code>
            </dd>
            <dt className="text-slate-500">行数 (header 除く)</dt>
            <dd className="text-slate-800">{preview.totalRows} 行</dd>
            <dt className="text-slate-500">valid 行数</dt>
            <dd className="text-slate-800">{preview.validRows} 行</dd>
            <dt className="text-slate-500">エラー / 警告</dt>
            <dd className="text-slate-800">{preview.errors.length} 件</dd>
          </dl>

          {preview.errors.length > 0 && (
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-danger-700 mb-1">
                エラー / 警告 (先頭 50 件)
              </h3>
              <ul className="text-xs text-danger-700 list-disc pl-5 max-h-48 overflow-auto">
                {preview.errors.slice(0, 50).map((e, idx) => (
                  <li key={idx}>
                    L{e.lineNumber}
                    {e.column ? ` [${e.column}]` : ''}: {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.rows.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-1">
                先頭 50 行 ({Math.min(50, preview.rows.length)} / {preview.totalRows})
              </h3>
              <div className="overflow-auto border border-slate-200 rounded max-h-96">
                <table className="text-xs w-full">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left text-slate-600 border-b border-slate-200">
                        #
                      </th>
                      {preview.headers.map((h) => (
                        <th
                          key={h}
                          className="px-2 py-1 text-left text-slate-600 border-b border-slate-200"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 50).map((row, idx) => (
                      <tr key={idx} className="even:bg-slate-50">
                        <td className="px-2 py-1 text-slate-500 border-b border-slate-100">
                          {idx + 2}
                        </td>
                        {preview.headers.map((h) => (
                          <td
                            key={h}
                            className="px-2 py-1 text-slate-800 border-b border-slate-100 align-top"
                          >
                            <span className="block max-w-xs truncate" title={row[h] ?? ''}>
                              {row[h] ?? ''}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}

      {photoProgress && submitState === 'uploading-photos' && (
        <Card padding="lg" className="mb-4">
          <h2 className="text-base font-bold text-slate-900 mb-2">写真アップロード中…</h2>
          <p className="text-sm text-slate-700 mb-2">
            {photoProgress.current} / {photoProgress.total}
            {photoProgress.currentFile ? ` (${photoProgress.currentFile})` : ''}
          </p>
          <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-600 transition-all"
              style={{
                width: `${photoProgress.total > 0 ? (photoProgress.current / photoProgress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </Card>
      )}

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">4. 実 import の実行</h2>
        <p className="text-xs text-slate-500 mb-3">
          importExecutor (post-* F24) / importParticipants (F25) / importSessions (F26) callable
          経由で本投入。master role 専用。確認 dialog で dryRun 件数を表示してから実行する。post-*
          は 上記カラムマッピングを適用してから実行できます。
        </p>
        {!isMaster && (
          <p className="text-xs text-warning-700 mb-2">
            実 import は master role 専用です (現 role: {authState.role ?? 'none'})。
          </p>
        )}
        {!SUPPORTED_EXEC_MODES.includes(mode) && (
          <p className="text-xs text-warning-700 mb-2">
            mode <code>{mode}</code> は本 callable 未対応。
          </p>
        )}
        {isPostMode(mode) && preview && !mappedRows && (
          <p className="text-xs text-warning-700 mb-2">
            先に「マッピング適用してプレビュー生成」を実行してください。
          </p>
        )}
        <Button
          type="button"
          onClick={() => {
            void handleRequestExecute();
          }}
          disabled={!execReady}
        >
          {submitState === 'dry-running'
            ? 'dryRun 中…'
            : submitState === 'uploading-photos'
              ? '写真アップロード中…'
              : submitState === 'submitting'
                ? '実行中…'
                : '実 import を実行 (確認あり)'}
        </Button>
        {submitError && (
          <p className="text-sm text-danger-700 mt-3" role="alert">
            {submitError}
          </p>
        )}
        {submitResult && (
          <div className="mt-3 text-sm">
            <p className="text-success-700 font-semibold">
              実行完了: {submitResult.primaryCount} 件 {submitResult.primaryLabel} /{' '}
              {submitResult.secondaryCount} 件 {submitResult.secondaryLabel} /{' '}
              {submitResult.errors.length} 件 エラー (全 {submitResult.totalRows} 行)
            </p>
            {submitResult.errors.length > 0 && (
              <ul className="text-xs text-danger-700 list-disc pl-5 mt-2 max-h-48 overflow-auto">
                {submitResult.errors.slice(0, 50).map((e, i) => (
                  <li key={i}>
                    L{e.lineNumber}: {e.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>

      {/* Drive 写真連携 (F28 / F29、master role 専用) */}
      {districtId && <DrivePhotosPanel districtId={districtId} enabled={isMaster} />}

      <ConfirmDialog
        open={confirmOpen}
        intent="warning"
        title="本投入を実行しますか?"
        message={
          dryRunResult ? (
            <div className="text-sm">
              <p>
                dryRun: {dryRunResult.validRows} 行が valid (全 {dryRunResult.totalRows} 行、
                {dryRunResult.errors.length} 件 エラー)。
              </p>
              {isPostMode(mode) && photoSource === 'local' && photoFiles.length > 0 && (
                <p className="text-slate-600 mt-2">
                  実行時にローカル写真 {photoMatchCount} 件を Storage
                  にアップロードしてから投入します。
                </p>
              )}
              <p className="text-slate-600 mt-2">
                重複検出後の skip 件数は dryRun では未計算。実行時に確定します。
              </p>
            </div>
          ) : (
            'dryRun 情報なし'
          )
        }
        confirmLabel="本投入を実行"
        cancelLabel="キャンセル"
        onConfirm={() => {
          void handleConfirmExecute();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`font-semibold ${color}`}>{value}</p>
    </div>
  );
}
